import { cn } from '@/utils';
import { SkeletonLoader } from '@/components';
import { tagVariants } from './Tag.styles';
import { TScoreSkillLevel } from '@/types';
import React from 'react';

export interface TagProps extends React.HTMLAttributes<HTMLSpanElement> {
  /**
   * Optional children to be rendered inside the tag. This can be used for
   * adding icons or other elements alongside the label.
   */
  children?: React.ReactNode;

  /**
   * The text content to display in the tag.
   */
  label?: string;

  /**
   * Whether to show a loading state for the tag. When true, the tag will
   * display a skeleton loader instead of its normal content. This is useful
   * when the tag's content is being fetched asynchronously and you want to
   * indicate that loading is in progress.
   * @default false
   */
  isLoading?: boolean;

  /**
   * Optional additional class name to apply to the tag when in loading state. This can
   * be used to customize the appearance of the skeleton loader or to apply specific
   * styles when loading.
   */
  loadingClassName?: string;

  /**
   * Tag variant which defines the visual style and color scheme.
   * Corresponds to canonical skill levels: level 1, intermediate, advanced, expert.
   * Default is the canonical level 1 variant.
   */
  variant?: TScoreSkillLevel | 'pass' | 'fail' | 'new' | 'not attempted' | 'default' | null;

  /**
   * Whether to render the tag with a border.
   */
  withBorder?: boolean;
}

/**
 * Base styles for the Tag component. These styles are always applied regardless
 * of the variant. They ensure a consistent shape, padding, font size, and text
 * color for all tags.
 */
export const Tag: React.FC<TagProps> = ({
  children,
  className,
  isLoading = false,
  label,
  loadingClassName,
  variant = 'default',
  withBorder = false,
  ...props
}) => {
  // Combine all class names into a single string. This includes base styles,
  // variant styles, border styles, and any additional classes passed via props.
  const classNames = cn(tagVariants({ variant, withBorder }), className);

  // If loading, render the skeleton loader
  // instead of the tag content
  if (isLoading) {
    return <SkeletonLoader className={loadingClassName} />;
  }

  // Render the tag with either the children
  // or the label as its content
  return (
    <span className={classNames} {...props}>
      {children ? children : label}
    </span>
  );
};

Tag.displayName = 'Tag';

export default Tag;
